import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { Loader2, CheckCircle2, AlertCircle, Clock, Circle } from 'lucide-react';
import { useRunsStore } from '@/stores/runs';
import type { ResearchRunSummary } from '@/services/api';

const RESEARCH_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All Types' },
  { value: 'customer_enrichment', label: 'Customer Enrichment' },
  { value: 'market_research', label: 'Market Research' },
  { value: 'buyer_scenario_generation', label: 'Buyer Scenarios' },
  { value: 'foundation_model_probing', label: 'Foundation Probes' },
  { value: 'taxonomy_expansion', label: 'Taxonomy Expansion' },
  { value: 'product_discovery', label: 'Product Discovery' },
  { value: 'brand_research', label: 'Brand Research' },
  { value: 'ad_corpus_analysis', label: 'Ad Corpus' },
  { value: 'keyword_intent_analysis', label: 'Keyword Intent' },
];

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'queued', label: 'Queued' },
  { value: 'in_progress', label: 'Running' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
  { value: 'cancelled', label: 'Cancelled' },
];

function timeAgo(isoStr?: string | null): string {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

export function RunList() {
  const navigate = useNavigate();
  const {
    runs,
    selectedRunSummary,
    filters,
    loading,
    setFilters,
    selectRun,
    fetchRuns,
    stopPolling,
  } = useRunsStore();

  useEffect(() => {
    fetchRuns();
    return () => stopPolling();
  }, [fetchRuns, stopPolling]);

  return (
    <div className="flex flex-col h-full bg-white border-r border-slate-200">
      <div className="p-3 space-y-2 border-b border-slate-100">
        <input
          type="text"
          placeholder="Client UID (optional)"
          value={filters.clientUid}
          onChange={(e) => setFilters({ clientUid: e.target.value })}
          onBlur={() => fetchRuns()}
          className="w-full bg-slate-50 text-sm text-slate-700 rounded-lg px-2 py-1.5 border border-slate-200 focus:border-brand-teal/50 focus:outline-none focus:ring-1 focus:ring-brand-teal/30 placeholder-slate-400"
        />
        <select
          value={filters.researchType}
          onChange={(e) => setFilters({ researchType: e.target.value })}
          className="w-full bg-slate-50 text-sm text-slate-700 rounded-lg px-2 py-1.5 border border-slate-200 focus:border-brand-teal/50 focus:outline-none focus:ring-1 focus:ring-brand-teal/30"
        >
          {RESEARCH_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => setFilters({ status: e.target.value })}
          className="w-full bg-slate-50 text-sm text-slate-700 rounded-lg px-2 py-1.5 border border-slate-200 focus:border-brand-teal/50 focus:outline-none focus:ring-1 focus:ring-brand-teal/30"
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading && runs.length === 0 ? (
          <div className="p-4 text-sm text-slate-400 text-center">Loading...</div>
        ) : runs.length === 0 ? (
          <div className="p-4 text-sm text-slate-400 text-center">
            No research runs found.
          </div>
        ) : (
          runs.map((r) => (
            <RunRow
              key={r.id}
              run={r}
              selected={selectedRunSummary?.id === r.id}
              onClick={() => {
                selectRun(r);
                navigate(`/knowledge/estate/runs/${r.id}`);
              }}
            />
          ))
        )}
      </div>
    </div>
  );
}

function StatusIcon({ status }: { status: string }) {
  switch (status) {
    case 'completed':
      return <CheckCircle2 size={12} className="text-emerald-500" />;
    case 'in_progress':
    case 'running':
      return <Loader2 size={12} className="text-brand-teal animate-spin" />;
    case 'failed':
    case 'cancelled':
      return <AlertCircle size={12} className="text-rose-500" />;
    case 'queued':
    case 'pending':
      return <Clock size={12} className="text-amber-500" />;
    default:
      return <Circle size={12} className="text-slate-400" />;
  }
}

function formatType(type: string): string {
  return type
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function RunRow({
  run,
  selected,
  onClick,
}: {
  run: ResearchRunSummary;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 transition-colors',
        selected && 'bg-slate-50 border-l-2 border-l-brand-teal',
      )}
    >
      <div className="flex items-center gap-2">
        <StatusIcon status={run.status} />
        <span className="text-sm font-medium text-slate-800 truncate">
          {formatType(run.research_type)}
        </span>
        <code className="ml-auto text-[10px] text-slate-400">{run.id.slice(0, 8)}</code>
      </div>
      <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 pl-4.5">
        {run.client_id && (
          <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium truncate max-w-[7rem]">
            {run.client_id}
          </span>
        )}
        <span>{run.entities_created_count} entities</span>
        <span className="ml-auto">{timeAgo(run.created_at)}</span>
      </div>
      {(run.status === 'in_progress' || run.status === 'running') && run.progress > 0 && (
        <div className="mt-1.5 pl-4.5">
          <div className="bg-slate-100 rounded-full h-1 overflow-hidden">
            <div
              className="h-full rounded-full bg-brand-teal transition-all duration-500"
              style={{ width: `${run.progress}%` }}
            />
          </div>
        </div>
      )}
    </button>
  );
}
